/*
 * Accessible lightbox for the PDP's "See it styled" collage and the Inspiration
 * gallery. It is only activated inside [data-mood] or [data-vmcm-lightbox], and
 * is created on demand so the large Shopify CDN image (or the video) is never
 * requested until a shopper opens a tile.
 *
 * Item contract, on [data-vmcm-mood-item]:
 *   data-vmcm-mood-src     high-resolution image URL (the CDN never upscales,
 *                          so the dialog shows it at no more than native size)
 *   data-vmcm-mood-video   video URL (shown with controls instead of the image)
 *   data-vmcm-mood-poster  optional poster for the video
 *   data-vmcm-mood-sound   present when this video should play with its sound on;
 *                          the dialog only ever opens from a click, so the browser
 *                          allows it, and a refusal still falls back to a silent
 *                          loop the shopper can unmute from the player controls
 *   data-vmcm-mood-alt     accessible description, also the dialog title
 */
(function () {
  'use strict';

  var selector = '[data-vmcm-mood-item]';
  var scopeSelector = '[data-mood], [data-vmcm-lightbox]';
  var modal, image, video, title, closeButton, previousButton, nextButton;
  var items = [], currentIndex = 0, lastTrigger = null, scrollState = null;

  function getItems(scope) {
    // A filtered-out gallery tile is [hidden]; stepping must skip it.
    return Array.prototype.slice.call(scope.querySelectorAll(selector)).filter(function (item) {
      return !item.closest('[hidden]');
    });
  }

  function lockScroll() {
    var root = document.scrollingElement || document.documentElement;
    var feed = document.querySelector('.vmcm-feed');
    var scrollbar = window.innerWidth - document.documentElement.clientWidth;
    scrollState = {
      rootX: window.pageXOffset,
      rootY: window.pageYOffset,
      feed: feed,
      feedTop: feed ? feed.scrollTop : 0,
      htmlStyle: document.documentElement.getAttribute('style'),
      bodyStyle: document.body.getAttribute('style')
    };
    document.documentElement.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = (-scrollState.rootY) + 'px';
    document.body.style.left = (-scrollState.rootX) + 'px';
    document.body.style.width = '100%';
    document.body.style.overflow = 'hidden';
    if (scrollbar) document.body.style.paddingRight = scrollbar + 'px';
    if (root) root.scrollTop = scrollState.rootY;
  }

  function restoreScroll() {
    if (!scrollState) return;
    if (scrollState.htmlStyle === null) document.documentElement.removeAttribute('style');
    else document.documentElement.setAttribute('style', scrollState.htmlStyle);
    if (scrollState.bodyStyle === null) document.body.removeAttribute('style');
    else document.body.setAttribute('style', scrollState.bodyStyle);
    if (scrollState.feed) scrollState.feed.scrollTop = scrollState.feedTop;
    window.scrollTo(scrollState.rootX, scrollState.rootY);
    scrollState = null;
  }

  function build() {
    modal = document.createElement('div');
    modal.className = 'vmcm-mood-lightbox';
    modal.hidden = true;
    modal.innerHTML =
      '<div class="vmcm-mood-lightbox__backdrop" data-vmcm-mood-close></div>' +
      '<section class="vmcm-mood-lightbox__dialog" role="dialog" aria-modal="true" aria-labelledby="vmcm-mood-lightbox-title" tabindex="-1">' +
        '<h2 class="vmcm-visually-hidden" id="vmcm-mood-lightbox-title">Enlarged view</h2>' +
        '<button class="vmcm-mood-lightbox__close" type="button" data-vmcm-mood-close aria-label="Close enlarged view">&times;</button>' +
        '<button class="vmcm-mood-lightbox__previous" type="button" aria-label="Previous">Previous</button>' +
        '<img class="vmcm-mood-lightbox__image" alt="" decoding="async">' +
        '<video class="vmcm-mood-lightbox__video" controls loop playsinline preload="metadata" hidden></video>' +
        '<button class="vmcm-mood-lightbox__next" type="button" aria-label="Next">Next</button>' +
      '</section>';
    document.body.appendChild(modal);
    image = modal.querySelector('.vmcm-mood-lightbox__image');
    video = modal.querySelector('.vmcm-mood-lightbox__video');
    title = modal.querySelector('#vmcm-mood-lightbox-title');
    closeButton = modal.querySelector('.vmcm-mood-lightbox__close');
    previousButton = modal.querySelector('.vmcm-mood-lightbox__previous');
    nextButton = modal.querySelector('.vmcm-mood-lightbox__next');
    Array.prototype.slice.call(modal.querySelectorAll('[data-vmcm-mood-close]')).forEach(function (element) {
      element.addEventListener('click', close);
    });
    previousButton.addEventListener('click', function () { show(currentIndex - 1); });
    nextButton.addEventListener('click', function () { show(currentIndex + 1); });
    modal.addEventListener('keydown', onKeydown);
  }

  function show(index) {
    currentIndex = (index + items.length) % items.length;
    var item = items[currentIndex];
    var alt = item.getAttribute('data-vmcm-mood-alt') || '';
    var videoSource = item.getAttribute('data-vmcm-mood-video');
    unloadMedia();
    if (videoSource) {
      var poster = item.getAttribute('data-vmcm-mood-poster');
      if (poster) video.poster = poster;
      video.setAttribute('aria-label', alt);
      var wantsSound = item.hasAttribute('data-vmcm-mood-sound');
      video.muted = !wantsSound;
      video.src = videoSource;
      video.hidden = false;
      var playAttempt = video.play();
      if (playAttempt && playAttempt.catch) {
        playAttempt.catch(function () {
          if (!wantsSound || video.muted) return;
          video.muted = true;
          var silentAttempt = video.play();
          if (silentAttempt && silentAttempt.catch) silentAttempt.catch(function () {});
        });
      }
    } else {
      image.src = item.getAttribute('data-vmcm-mood-src') || '';
      image.alt = alt;
      image.hidden = false;
    }
    title.textContent = alt || 'Enlarged view';
    var hasMultiple = items.length > 1;
    previousButton.hidden = !hasMultiple;
    nextButton.hidden = !hasMultiple;
  }

  function unloadMedia() {
    image.hidden = true;
    image.removeAttribute('src');
    video.hidden = true;
    video.pause();
    video.removeAttribute('poster');
    video.removeAttribute('src');
    video.load();
  }

  function open(trigger) {
    var scope = trigger.closest(scopeSelector);
    if (!scope) return;
    if (!modal) build();
    items = getItems(scope);
    var item = trigger.closest(selector);
    currentIndex = Math.max(0, items.indexOf(item));
    // An image is not focusable; hand focus back to the tile's own button.
    lastTrigger = trigger.matches('button, a') ? trigger : ((item && item.querySelector('button[data-vmcm-mood-trigger]')) || trigger);
    show(currentIndex);
    lockScroll();
    modal.hidden = false;
    modal.classList.add('is-open');
    closeButton.focus();
  }

  function close() {
    if (!modal || modal.hidden) return;
    modal.classList.remove('is-open');
    modal.hidden = true;
    unloadMedia();
    restoreScroll();
    if (lastTrigger && lastTrigger.focus) lastTrigger.focus();
  }

  function onKeydown(event) {
    if (event.key === 'Escape') { event.preventDefault(); close(); return; }
    // Arrow keys belong to the player while it has focus (they scrub the video).
    var stepping = items.length > 1 && event.target !== video;
    if (stepping && event.key === 'ArrowLeft') { event.preventDefault(); show(currentIndex - 1); return; }
    if (stepping && event.key === 'ArrowRight') { event.preventDefault(); show(currentIndex + 1); return; }
    if (event.key !== 'Tab') return;
    var focusable = Array.prototype.slice.call(modal.querySelectorAll('button:not([hidden]), video:not([hidden])'));
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  }

  document.addEventListener('click', function (event) {
    var trigger = event.target.closest && event.target.closest('[data-vmcm-mood-trigger]');
    if (!trigger || !trigger.closest(scopeSelector)) return;
    event.preventDefault();
    open(trigger);
  });
})();
